// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Header, Button, Table, Icon, Message, Loader, Dropdown, Label, Segment } from 'semantic-ui-react';
import { emailTemplateApi, EmailTemplate } from '../api/emailTemplateApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import { providerApi, Provider } from '../api/providerApi';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';

function EmailTemplateListPage() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedBrokerage, setSelectedBrokerage] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedPolicyType, setSelectedPolicyType] = useState<string>('');

  const navigate = useNavigate();

  // Load reference data
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [brokeragesData, providersData, policyTypesData] = await Promise.all([
        brokerageApi.getAll(),
        providerApi.getAll(),
        policyTypeApi.getAll()
      ]);
      setBrokerages(brokeragesData.content);
      setProviders(providersData.content);
      setPolicyTypes(policyTypesData.content);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await emailTemplateApi.getAll({
        templateType: selectedType || undefined,
        brokerageId: selectedBrokerage || undefined,
        providerId: selectedProvider || undefined,
        policyTypeId: selectedPolicyType || undefined
      });
      setTemplates(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedBrokerage, selectedProvider, selectedPolicyType]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      setError('Cannot delete DEFAULT system templates');
      return;
    }

    if (!globalThis.confirm('Are you sure you want to delete this email template?')) {
      return;
    }

    try {
      await emailTemplateApi.delete(id);
      await loadTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete email template');
    }
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedBrokerage('');
    setSelectedProvider('');
    setSelectedPolicyType('');
  };

  const templateTypeOptions = [
    { key: '', text: 'All Types', value: '' },
    { key: 'ADVISOR_LOA_PROGRESS', text: 'Advisor Progress', value: 'ADVISOR_LOA_PROGRESS' },
    { key: 'ADVISOR_LOA_COMPLETED', text: 'Advisor Completion', value: 'ADVISOR_LOA_COMPLETED' },
    { key: 'PROVIDER_LOA_NOTIFICATION', text: 'Provider Notification', value: 'PROVIDER_LOA_NOTIFICATION' }
  ];

  const brokerageOptions = [
    { key: '', text: 'All Brokerages', value: '' },
    ...brokerages.map(b => ({ key: b.id, text: b.name, value: b.id }))
  ];

  const providerOptions = [
    { key: '', text: 'All Providers', value: '' },
    ...providers.map(p => ({ key: p.id, text: p.name, value: p.id }))
  ];

  const policyTypeOptions = [
    { key: '', text: 'All Policy Types', value: '' },
    ...policyTypes.map(pt => ({ key: pt.id, text: pt.name, value: pt.id }))
  ];

  const getTemplateLevelColor = (level: string) => {
    switch (level) {
      case 'DEFAULT': return 'grey';
      case 'BROKER': return 'blue';
      case 'PROVIDER': return 'teal';
      case 'POLICY': return 'olive';
      case 'BROKER_PROVIDER': return 'violet';
      case 'BROKER_POLICY': return 'purple';
      default: return 'grey';
    }
  };

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case 'ADVISOR_LOA_PROGRESS': return 'Advisor Progress';
      case 'ADVISOR_LOA_COMPLETED': return 'Advisor Completion';
      case 'PROVIDER_LOA_NOTIFICATION': return 'Provider Notification';
      default: return type;
    }
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='mail' />
        <Header.Content>
          System-Wide Email Templates
          <Header.Subheader>
            Manage DEFAULT, PROVIDER, and POLICY-level templates.
            For brokerage-specific templates, use entity edit pages.
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Message info>
        <Message.Header>Contextual Template Configuration</Message.Header>
        <p>
          <strong>BROKER</strong> templates: Configure in <Icon name='building' />Brokerage Edit page<br/>
          <strong>BROKER_PROVIDER</strong> templates: Configure in <Icon name='factory' />Provider Configuration page<br/>
          <strong>BROKER_POLICY</strong> templates: Configure in <Icon name='file alternate' />Policy Type Configuration page
        </p>
      </Message>

      {/* Filters */}
      <Segment>
        <Header as='h3'>
          <Icon name='filter' />
          Filters
        </Header>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label htmlFor="filter-template-type" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Template Type</label>
            <Dropdown
              id="filter-template-type"
              fluid
              selection
              options={templateTypeOptions}
              value={selectedType}
              onChange={(_e, data) => setSelectedType(data.value as string)}
              placeholder='Filter by type'
            />
          </div>
          <div>
            <label htmlFor="filter-brokerage" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Brokerage</label>
            <Dropdown
              id="filter-brokerage"
              fluid
              selection
              search
              options={brokerageOptions}
              value={selectedBrokerage}
              onChange={(_e, data) => setSelectedBrokerage(data.value as string)}
              placeholder='Filter by brokerage'
            />
          </div>
          <div>
            <label htmlFor="filter-provider" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Provider</label>
            <Dropdown
              id="filter-provider"
              fluid
              selection
              search
              options={providerOptions}
              value={selectedProvider}
              onChange={(_e, data) => setSelectedProvider(data.value as string)}
              placeholder='Filter by provider'
            />
          </div>
          <div>
            <label htmlFor="filter-policy-type" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Policy Type</label>
            <Dropdown
              id="filter-policy-type"
              fluid
              selection
              search
              options={policyTypeOptions}
              value={selectedPolicyType}
              onChange={(_e, data) => setSelectedPolicyType(data.value as string)}
              placeholder='Filter by policy type'
            />
          </div>
        </div>
        <Button size='small' onClick={clearFilters}>
          <Icon name='remove' />
          Clear Filters
        </Button>
        <Button primary size='small' onClick={() => navigate('/email-templates/new')} floated='right'>
          <Icon name='plus' />
          Create Template
        </Button>
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading email templates...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Level</Table.HeaderCell>
                <Table.HeaderCell>Scope</Table.HeaderCell>
                <Table.HeaderCell>Subject Template</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Version</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {templates.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='7' textAlign='center'>
                    No email templates found
                  </Table.Cell>
                </Table.Row>
              ) : (
                templates.map((template) => (
                  <Table.Row key={template.id}>
                    <Table.Cell>
                      <Label color='blue' size='small'>
                        {getTemplateTypeLabel(template.templateType)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={getTemplateLevelColor(template.templateLevel)} size='small'>
                        {template.templateLevel}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {template.brokerageName && (
                        <div><Icon name='building' />{template.brokerageName}</div>
                      )}
                      {template.providerName && (
                        <div><Icon name='factory' />{template.providerName}</div>
                      )}
                      {template.policyTypeName && (
                        <div><Icon name='file alternate' />{template.policyTypeName}</div>
                      )}
                      {!template.brokerageName && !template.providerName && !template.policyTypeName && (
                        <span style={{ color: '#999' }}>Global</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: '0.85em' }}>{template.subjectTemplate}</code>
                    </Table.Cell>
                    <Table.Cell textAlign='center'>
                      {template.isActive ? (
                        <Label color='green' size='tiny'>
                          <Icon name='check circle' />Active
                        </Label>
                      ) : (
                        <Label color='grey' size='tiny'>
                          <Icon name='pause circle' />Inactive
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign='center'>v{template.version || 1}</Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        <Button
                          icon='eye'
                          color='teal'
                          onClick={() => navigate(`/email-templates/${template.id}/preview`)}
                          title='Preview'
                        />
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/email-templates/${template.id}/edit`)}
                          title='Edit'
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDelete(template.id!, template.templateLevel === 'DEFAULT')}
                          disabled={template.templateLevel === 'DEFAULT'}
                          title={template.templateLevel === 'DEFAULT' ? 'Cannot delete DEFAULT templates' : 'Delete'}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          <div style={{ marginTop: '20px', color: '#666', textAlign: 'center' }}>
            Showing {templates.length} template{templates.length === 1 ? '' : 's'}
          </div>
        </>
      )}
    </Container>
  );
}

export default EmailTemplateListPage;
